// ─── Room Manager ─────────────────────────────────────────────────────────────

import {
  TILE_SIZE,
  TileType,
  DEPARTMENT_FLOOR_COLORS,
  DEPARTMENT_COLORS,
  type RoomData,
  type Interactable,
  type Position,
} from "./constants";
import type { DepartmentState, SectionState } from "@/stores/game-store";
import {
  loadMap as registryLoadMap,
  registerOverworld,
  checkExits as registryCheckExits,
} from "./world-map";
import {
  runTransition,
  TRANSITION_DEFAULT_MS,
  type TransitionKind,
} from "./battle-transitions";
import type { MapExit } from "@/data/maps/route1";

/** Callbacks the engine wires up so the transition can mutate runtime state. */
export interface MapTransitionCallbacks {
  /**
   * Render overlay update — caller forwards progress 0..1 to React state
   * driving the wipe overlay. Optional: useful in tests / non-DOM contexts.
   */
  onProgress?: (progress: number) => void;
  /**
   * Player teleport hook. Fires at the halfway point, after the active map
   * has been swapped. Coordinates are world pixels.
   */
  onTeleport?: (x: number, y: number, newMapId: string) => void;
}

/**
 * RoomManager handles room transitions between overworld, department interiors,
 * and section views.
 */
export class RoomManager {
  private currentRoom: RoomData | null = null;
  private overworldRoom: RoomData | null = null;
  private departmentRooms: Map<string, RoomData> = new Map();
  private sectionRooms: Map<string, RoomData> = new Map();

  // Active map id, kept in sync with currentRoom for the multi-map registry.
  private currentMapId: string = "overworld";
  private transitionCallbacks: MapTransitionCallbacks = {};

  /**
   * Set the overworld room data (generated by WorldMap). Also registers the
   * room with the multi-map registry so `loadMap("overworld")` works.
   */
  setOverworld(room: RoomData): void {
    this.overworldRoom = room;
    registerOverworld(room);
    if (!this.currentRoom || this.currentMapId === "overworld") {
      this.currentRoom = room;
      this.currentMapId = room.id;
    }
  }

  /**
   * Wire transition callbacks (progress + teleport). Optional — the
   * transition itself works headlessly without them.
   */
  setTransitionCallbacks(cb: MapTransitionCallbacks): void {
    this.transitionCallbacks = cb;
  }

  /**
   * Load any map from the registry by id ('overworld' | 'route_1' |
   * 'town_center' | 'beach_cave'). Returns null if unknown or not yet ready.
   */
  loadMap(mapId: string): RoomData | null {
    return registryLoadMap(mapId);
  }

  /** Active map id (mirrors getCurrentRoom().id, but never null). */
  getCurrentMapId(): string {
    return this.currentMapId;
  }

  /**
   * Tile-aligned exit check for the active (or specified) map. Returns the
   * matching exit when the player is standing on one, otherwise null.
   * GameCanvas should call this each tick.
   */
  checkExits(
    playerX: number,
    playerY: number,
    currentMapId: string = this.currentMapId,
  ): MapExit | null {
    return registryCheckExits(playerX, playerY, currentMapId);
  }

  /**
   * Run a wipe transition, swap the active map at the halfway point, teleport
   * the player to (targetX, targetY) in tile coords, and resolve when the
   * transition completes. SSR-safe via runTransition's window guard.
   */
  triggerMapTransition(
    targetMapId: string,
    targetX: number,
    targetY: number,
    transitionKind: TransitionKind = "wipe-down",
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      const target = this.loadMap(targetMapId);
      if (!target) {
        // Unknown map id — resolve immediately to avoid hanging callers.
        resolve();
        return;
      }

      const cb = this.transitionCallbacks;
      runTransition(
        {
          kind: transitionKind,
          durationMs: TRANSITION_DEFAULT_MS[transitionKind],
          onHalfway: () => {
            // Swap active map + teleport player to tile coords (pixels).
            this.currentRoom = target;
            this.currentMapId = target.id;
            const px = targetX * TILE_SIZE;
            const py = targetY * TILE_SIZE;
            cb.onTeleport?.(px, py, target.id);
          },
          onComplete: () => {
            resolve();
          },
        },
        (progress) => {
          cb.onProgress?.(progress);
        },
      );
    });
  }

  /**
   * Generate and cache a department interior room.
   */
  generateDepartmentRoom(
    dept: DepartmentState,
    sections: SectionState[],
  ): RoomData {
    const width = 20; // tiles
    const height = 16; // tiles

    const tileMap: number[][] = [];
    const collisionMap: boolean[][] = [];

    for (let row = 0; row < height; row++) {
      tileMap[row] = [];
      collisionMap[row] = [];
      for (let col = 0; col < width; col++) {
        // Walls on the border
        if (row === 0 || row === height - 1 || col === 0 || col === width - 1) {
          tileMap[row][col] = TileType.Wall;
          collisionMap[row][col] = true;
        } else {
          tileMap[row][col] = TileType.BuildingFloor;
          collisionMap[row][col] = false;
        }
      }
    }

    // Door at bottom center to exit back to overworld
    const doorCol = Math.floor(width / 2);
    tileMap[height - 1][doorCol] = TileType.Door;
    collisionMap[height - 1][doorCol] = false;
    tileMap[height - 1][doorCol + 1] = TileType.Door;
    collisionMap[height - 1][doorCol + 1] = false;

    // Place section areas inside the room
    const interactables: Interactable[] = [];
    const deptSections = sections.filter((s) => s.departmentId === dept.id);

    deptSections.forEach((section, i) => {
      const sx = 2 + (i % 3) * 6;
      const sy = 2 + Math.floor(i / 3) * 5;

      // Mark section area on tile map
      for (let r = sy; r < sy + 3 && r < height - 1; r++) {
        for (let c = sx; c < sx + 5 && c < width - 1; c++) {
          tileMap[r][c] = TileType.BuildingExterior;
        }
      }

      interactables.push({
        id: section.id,
        type: "section",
        position: { x: sx * TILE_SIZE, y: sy * TILE_SIZE },
        width: 5 * TILE_SIZE,
        height: 3 * TILE_SIZE,
        data: { name: section.name, type: section.type },
      });
    });

    // Exit door interactable
    interactables.push({
      id: `exit_${dept.id}`,
      type: "building",
      position: { x: doorCol * TILE_SIZE, y: (height - 1) * TILE_SIZE },
      width: 2 * TILE_SIZE,
      height: TILE_SIZE,
      data: { action: "exit", targetRoom: "overworld" },
    });

    const room: RoomData = {
      id: dept.id,
      name: dept.name,
      type: "department",
      tileMap,
      collisionMap,
      interactables,
      width,
      height,
    };

    this.departmentRooms.set(dept.id, room);
    return room;
  }

  /**
   * Generate and cache a section interior room.
   */
  generateSectionRoom(section: SectionState): RoomData {
    const width = 16;
    const height = 12;

    const tileMap: number[][] = [];
    const collisionMap: boolean[][] = [];

    for (let row = 0; row < height; row++) {
      tileMap[row] = [];
      collisionMap[row] = [];
      for (let col = 0; col < width; col++) {
        if (row === 0 || row === height - 1 || col === 0 || col === width - 1) {
          tileMap[row][col] = TileType.Wall;
          collisionMap[row][col] = true;
        } else {
          tileMap[row][col] = TileType.BuildingFloor;
          collisionMap[row][col] = false;
        }
      }
    }

    // Exit door
    const doorCol = Math.floor(width / 2);
    tileMap[height - 1][doorCol] = TileType.Door;
    collisionMap[height - 1][doorCol] = false;

    // Workstation positions
    const interactables: Interactable[] = [];

    // 4 workstations in a grid
    const stations = [
      { col: 3, row: 3 },
      { col: 9, row: 3 },
      { col: 3, row: 7 },
      { col: 9, row: 7 },
    ];

    stations.forEach((st, i) => {
      // Mark workstation tile
      tileMap[st.row][st.col] = TileType.TikiBar; // reuse as workstation tile
      collisionMap[st.row][st.col] = true;

      interactables.push({
        id: `ws_${section.id}_${i}`,
        type: "section",
        position: { x: st.col * TILE_SIZE, y: st.row * TILE_SIZE },
        width: TILE_SIZE * 2,
        height: TILE_SIZE * 2,
        data: { workstationIndex: i },
      });
    });

    // Exit interactable
    interactables.push({
      id: `exit_${section.id}`,
      type: "building",
      position: { x: doorCol * TILE_SIZE, y: (height - 1) * TILE_SIZE },
      width: TILE_SIZE,
      height: TILE_SIZE,
      data: { action: "exit", targetRoom: section.departmentId },
    });

    const room: RoomData = {
      id: section.id,
      name: section.name,
      type: "section",
      tileMap,
      collisionMap,
      interactables,
      width,
      height,
    };

    this.sectionRooms.set(section.id, room);
    return room;
  }

  /**
   * Transition to a room by ID.
   * Returns the player's spawn position for the new room.
   */
  transition(roomId: string): Position {
    if (roomId === "overworld" && this.overworldRoom) {
      this.currentRoom = this.overworldRoom;
      this.currentMapId = "overworld";
      return { x: 400, y: 300 };
    }

    const dept = this.departmentRooms.get(roomId);
    if (dept) {
      this.currentRoom = dept;
      this.currentMapId = roomId;
      // Spawn near the door
      const doorCol = Math.floor(dept.width / 2);
      return { x: doorCol * TILE_SIZE, y: (dept.height - 3) * TILE_SIZE };
    }

    const section = this.sectionRooms.get(roomId);
    if (section) {
      this.currentRoom = section;
      this.currentMapId = roomId;
      const doorCol = Math.floor(section.width / 2);
      return { x: doorCol * TILE_SIZE, y: (section.height - 3) * TILE_SIZE };
    }

    // Multi-map registry fallback (route_1, town_center, beach_cave, etc.)
    const registryRoom = this.loadMap(roomId);
    if (registryRoom) {
      this.currentRoom = registryRoom;
      this.currentMapId = roomId;
      return { x: 100, y: 100 };
    }

    // Fallback
    return { x: 100, y: 100 };
  }

  getCurrentRoom(): RoomData | null {
    return this.currentRoom;
  }

  getCollisionMap(): boolean[][] {
    return this.currentRoom?.collisionMap ?? [];
  }

  getInteractables(): Interactable[] {
    return this.currentRoom?.interactables ?? [];
  }
}
